import { makeDictionary, createPolicies } from '@/domain/restriction/engine';

export const RIGHTS = {
  IS_AUTHORIZED: 'IS_AUTHORIZED',
  IS_NOT_AUTHORIZED: 'IS_NOT_AUTHORIZED',
  IS_ACCOUNT: 'IS_ACCOUNT',
  IS_LIMITED_ACCOUNT: 'IS_LIMITED_ACCOUNT',
  IS_RESTRICTED_ACCOUNTANT: 'IS_RESTRICTED_ACCOUNTANT',
  IS_NOT_RESTRICTED_ACCOUNTANT: 'IS_NOT_RESTRICTED_ACCOUNTANT',
  IS_READ_ONLY_ACCOUNTANT: 'IS_READ_ONLY_ACCOUNTANT',
  IS_USER: 'IS_USER',
  IS_NOT_USER: 'IS_NOT_USER',
  IS_NOT_CONFIDENTIAL_USER: 'IS_NOT_CONFIDENTIAL_USER',
  IS_CONFIDENTIAL_USER: 'IS_CONFIDENTIAL_USER',
  IS_RESTRICTED_USER: 'IS_RESTRICTED_USER',
  IS_NOT_RESTRICTED_USER: 'IS_NOT_RESTRICTED_USER',
  IS_CONFIDENTIAL_RESTRICTED_USER: 'IS_CONFIDENTIAL_RESTRICTED_USER',
  IS_NOT_CONFIDENTIAL_RESTRICTED_USER: 'IS_NOT_CONFIDENTIAL_RESTRICTED_USER',
  IS_SUPPLIER_USER: 'IS_SUPPLIER_USER',
  IS_NOT_SUPPLIER_USER: 'IS_NOT_SUPPLIER_USER',
  IS_COMPANY_SUPPORT_GENERAL_DOC_ONLY: 'IS_COMPANY_SUPPORT_GENERAL_DOC_ONLY',
  IS_COMPANY_SUPPORT_FINANCIAL_DOC: 'IS_COMPANY_SUPPORT_FINANCIAL_DOC',
  IS_ADMIN: 'IS_ADMIN',
  IS_NOT_ADMIN: 'IS_NOT_ADMIN',
  IS_EXPIRED: 'IS_EXPIRED',
  IS_CURRENT_DOCUMENT_FINANCIAL: 'IS_CURRENT_DOCUMENT_FINANCIAL',
  IS_ACCEPTED_DOCUMENT: 'IS_ACCEPTED_DOCUMENT',
  IS_NOT_ACCEPTED_DOCUMENT: 'IS_NOT_ACCEPTED_DOCUMENT',
  IS_DOCUMENT_BEING_PROCESSED: 'IS_DOCUMENT_BEING_PROCESSED',
  IS_NOT_DOCUMENT_BEING_PROCESSED: 'IS_NOT_DOCUMENT_BEING_PROCESSED',
  IS_DOCUMENT_SCHEDULED_ACCEPTANCE: 'IS_DOCUMENT_SCHEDULED_ACCEPTANCE',
  ALLOW_FRESHDESK: 'ALLOW_FRESHDESK',
  ONLY_ADMIN_CAN_CHANGE_AF: 'ONLY_ADMIN_CAN_CHANGE_AF',
  NOT_ONLY_ADMIN_CAN_CHANGE_AF: 'NOT_ONLY_ADMIN_CAN_CHANGE_AF',
  IS_PAY_MODULE_AVAILABLE_FOR_THIS_COMPANY: 'IS_PAY_MODULE_AVAILABLE_FOR_THIS_COMPANY',
  CAN_PREPARE_PAYMENT_FOR_NON_PUBLISHED_INVOICES: 'CAN_PREPARE_PAYMENT_FOR_NON_PUBLISHED_INVOICES',
  PAY_IS_OWNER: 'PAY_IS_OWNER',
  PAY_IS_NOT_OWNER: 'PAY_IS_NOT_OWNER',
  PAY_IS_CONTROLLER: 'PAY_IS_CONTROLLER',
  PAY_IS_NOT_CONTROLLER: 'PAY_IS_NOT_CONTROLLER',
  PAY_IS_APPROVER: 'PAY_IS_APPROVER',
  PAY_IS_NOT_APPROVER: 'PAY_IS_NOT_APPROVER',
  PAY_HAS_NON_DISCONNECTED_PAYMENT_PROVIDER: 'PAY_HAS_NON_DISCONNECTED_PAYMENT_PROVIDER',
  PAY_ALL_DISCONNECTED_PAYMENT_PROVIDER: 'PAY_ALL_DISCONNECTED_PAYMENT_PROVIDER',
  PAY_HAS_CONNECTED_PAYMENT_PROVIDER: 'PAY_HAS_CONNECTED_PAYMENT_PROVIDER',
} as const;

type Right = (typeof RIGHTS)[keyof typeof RIGHTS];

export const dictionary = makeDictionary(Object.keys(RIGHTS));

const policy = (...arr: Array<Right | Right[]>) => createPolicies(dictionary, arr);

export const IS_AUTHORIZED = policy(RIGHTS.IS_AUTHORIZED);
export const IS_CONFIDENTIAL_USER = policy(RIGHTS.IS_CONFIDENTIAL_USER, RIGHTS.IS_CONFIDENTIAL_RESTRICTED_USER);
export const IS_RESTRICTED_USER = policy(RIGHTS.IS_RESTRICTED_USER);
export const IS_CONFIDENTIAL_RESTRICTED_USER = policy(RIGHTS.IS_CONFIDENTIAL_RESTRICTED_USER);
export const IS_SUPPLIER_USER = policy(RIGHTS.IS_SUPPLIER_USER);
export const IS_NOT_AUTHORIZED = policy(RIGHTS.IS_NOT_AUTHORIZED, RIGHTS.IS_EXPIRED);
export const IS_ACCOUNT = policy(
  RIGHTS.IS_ACCOUNT,
  RIGHTS.IS_LIMITED_ACCOUNT,
  RIGHTS.IS_RESTRICTED_ACCOUNTANT,
  RIGHTS.IS_READ_ONLY_ACCOUNTANT,
);
export const IS_LIMITED_ACCOUNT = policy(RIGHTS.IS_LIMITED_ACCOUNT);
export const IS_RESTRICTED_ACCOUNTANT = policy(RIGHTS.IS_RESTRICTED_ACCOUNTANT);
export const IS_READ_ONLY_ACCOUNTANT = policy(RIGHTS.IS_READ_ONLY_ACCOUNTANT);
export const IS_USER = policy(
  RIGHTS.IS_USER,
  RIGHTS.IS_RESTRICTED_USER,
  RIGHTS.IS_CONFIDENTIAL_USER,
  RIGHTS.IS_CONFIDENTIAL_RESTRICTED_USER,
  RIGHTS.IS_SUPPLIER_USER,
);
export const IS_BOOKKEEPER_IN_FINANCIAL_COMPANY = policy(
  [RIGHTS.IS_ACCOUNT, RIGHTS.IS_COMPANY_SUPPORT_FINANCIAL_DOC],
  [RIGHTS.IS_LIMITED_ACCOUNT, RIGHTS.IS_COMPANY_SUPPORT_FINANCIAL_DOC],
  [RIGHTS.IS_RESTRICTED_ACCOUNTANT, RIGHTS.IS_COMPANY_SUPPORT_FINANCIAL_DOC],
  [RIGHTS.IS_READ_ONLY_ACCOUNTANT, RIGHTS.IS_COMPANY_SUPPORT_FINANCIAL_DOC],
);
export const IS_BK_WITHOUT_ADMIN_PERMISSIONS = policy(
  [RIGHTS.IS_ACCOUNT, RIGHTS.IS_NOT_ADMIN],
  [RIGHTS.IS_LIMITED_ACCOUNT, RIGHTS.IS_NOT_ADMIN],
  [RIGHTS.IS_RESTRICTED_ACCOUNTANT, RIGHTS.IS_NOT_ADMIN],
  [RIGHTS.IS_READ_ONLY_ACCOUNTANT, RIGHTS.IS_NOT_ADMIN],
);
export const IS_COMPANY_SUPPORT_FINANCIAL_DOC = policy(RIGHTS.IS_COMPANY_SUPPORT_FINANCIAL_DOC);
export const IS_ADMIN = policy(RIGHTS.IS_ADMIN);
export const IS_EXPIRED = policy(RIGHTS.IS_EXPIRED);
export const IS_DOCUMENT_SCHEDULED_ACCEPTANCE = policy(RIGHTS.IS_DOCUMENT_SCHEDULED_ACCEPTANCE);
export const IS_WORK_WITH_FINANCIAL_DOCUMENT = policy([
  RIGHTS.IS_NOT_USER,
  RIGHTS.IS_NOT_CONFIDENTIAL_USER,
  RIGHTS.IS_COMPANY_SUPPORT_FINANCIAL_DOC,
]);

export const IS_ALLOW_MERGE_DOCUMENTS = policy(
  RIGHTS.IS_USER,
  RIGHTS.IS_RESTRICTED_USER,
  RIGHTS.IS_CONFIDENTIAL_USER,
  RIGHTS.IS_CONFIDENTIAL_RESTRICTED_USER,
  RIGHTS.IS_ACCOUNT,
  RIGHTS.IS_LIMITED_ACCOUNT,
  RIGHTS.IS_RESTRICTED_ACCOUNTANT,
  RIGHTS.IS_READ_ONLY_ACCOUNTANT,
);

export const IS_WORK_ON_WITH_CURRENT_DOCUMENT_AS_FINANCIAL = policy([
  RIGHTS.IS_CURRENT_DOCUMENT_FINANCIAL,
  RIGHTS.IS_NOT_USER,
  RIGHTS.IS_NOT_RESTRICTED_USER,
  RIGHTS.IS_NOT_CONFIDENTIAL_USER,
  RIGHTS.IS_NOT_CONFIDENTIAL_RESTRICTED_USER,
]);

export const CAN_CHECK_DUPLICATE_ERP_DOCS = policy([RIGHTS.IS_CURRENT_DOCUMENT_FINANCIAL, RIGHTS.IS_NOT_SUPPLIER_USER]);

export const IS_ACCEPTABLE_DOCUMENT = policy([
  RIGHTS.IS_CURRENT_DOCUMENT_FINANCIAL,
  RIGHTS.IS_NOT_USER,
  RIGHTS.IS_NOT_CONFIDENTIAL_USER,
  RIGHTS.IS_NOT_RESTRICTED_USER,
  RIGHTS.IS_NOT_SUPPLIER_USER,
  RIGHTS.IS_NOT_CONFIDENTIAL_RESTRICTED_USER,
  RIGHTS.IS_COMPANY_SUPPORT_FINANCIAL_DOC,
  RIGHTS.IS_NOT_ACCEPTED_DOCUMENT,
  RIGHTS.IS_NOT_DOCUMENT_BEING_PROCESSED,
]);

export const ALLOW_FRESHDESK = policy(RIGHTS.ALLOW_FRESHDESK);

export const CAN_MANAGE_BULK_AF = policy(
  [RIGHTS.IS_ADMIN, RIGHTS.ONLY_ADMIN_CAN_CHANGE_AF],
  [RIGHTS.IS_AUTHORIZED, RIGHTS.NOT_ONLY_ADMIN_CAN_CHANGE_AF],
);

export const CAN_MANAGE_APPROVALS_SETTINGS = policy(
  [RIGHTS.IS_ADMIN, RIGHTS.ONLY_ADMIN_CAN_CHANGE_AF],
  [RIGHTS.IS_ACCOUNT, RIGHTS.NOT_ONLY_ADMIN_CAN_CHANGE_AF],
);

export const IS_PAY_MODULE_AVAILABLE_FOR_THIS_COMPANY = policy(RIGHTS.IS_PAY_MODULE_AVAILABLE_FOR_THIS_COMPANY);

export const CAN_SEND_TO_DOKKA_PAY = policy([
  RIGHTS.IS_NOT_USER,
  RIGHTS.IS_NOT_CONFIDENTIAL_USER,
  RIGHTS.IS_NOT_RESTRICTED_USER,
  RIGHTS.IS_NOT_SUPPLIER_USER,
  RIGHTS.IS_NOT_CONFIDENTIAL_RESTRICTED_USER,
  RIGHTS.IS_COMPANY_SUPPORT_FINANCIAL_DOC,
  RIGHTS.IS_PAY_MODULE_AVAILABLE_FOR_THIS_COMPANY,
]);

const CAN_PREPARE_PAYMENT_BASE_RIGHTS = [
  RIGHTS.IS_CURRENT_DOCUMENT_FINANCIAL,
  RIGHTS.IS_NOT_USER,
  RIGHTS.IS_NOT_CONFIDENTIAL_USER,
  RIGHTS.IS_NOT_RESTRICTED_USER,
  RIGHTS.IS_NOT_SUPPLIER_USER,
  RIGHTS.IS_NOT_CONFIDENTIAL_RESTRICTED_USER,
  RIGHTS.IS_COMPANY_SUPPORT_FINANCIAL_DOC,
  RIGHTS.IS_NOT_DOCUMENT_BEING_PROCESSED,
  RIGHTS.IS_PAY_MODULE_AVAILABLE_FOR_THIS_COMPANY,
];

// Show "Prepare payment" for accepted (published) documents, OR for non-published
// documents when the company enables `enable_prepare_payment_for_non_published_invoices`.
export const CAN_PREPARE_PAYMENT = policy(
  [...CAN_PREPARE_PAYMENT_BASE_RIGHTS, RIGHTS.IS_ACCEPTED_DOCUMENT],
  [...CAN_PREPARE_PAYMENT_BASE_RIGHTS, RIGHTS.CAN_PREPARE_PAYMENT_FOR_NON_PUBLISHED_INVOICES],
);

export const PAY_IS_OWNER = policy(RIGHTS.PAY_IS_OWNER);
export const PAY_IS_NOT_OWNER = policy(RIGHTS.PAY_IS_NOT_OWNER);
export const PAY_IS_CONTROLLER = policy(RIGHTS.PAY_IS_CONTROLLER);
export const PAY_IS_NOT_CONTROLLER = policy(RIGHTS.PAY_IS_NOT_CONTROLLER);
export const PAY_IS_APPROVER = policy(RIGHTS.PAY_IS_APPROVER);
export const PAY_IS_NOT_APPROVER = policy(RIGHTS.PAY_IS_NOT_APPROVER);

export const CAN_ACCESS_TO_PAY_SETTINGS = policy(
  [RIGHTS.PAY_ALL_DISCONNECTED_PAYMENT_PROVIDER],
  [RIGHTS.PAY_HAS_NON_DISCONNECTED_PAYMENT_PROVIDER, RIGHTS.PAY_IS_OWNER],
  [RIGHTS.PAY_HAS_NON_DISCONNECTED_PAYMENT_PROVIDER, RIGHTS.PAY_IS_CONTROLLER],
);

export const CAN_ACCESS_TO_PAY_SETTINGS_BENEFICIARIES = policy(
  [RIGHTS.PAY_HAS_CONNECTED_PAYMENT_PROVIDER, RIGHTS.PAY_IS_OWNER],
  [RIGHTS.PAY_HAS_CONNECTED_PAYMENT_PROVIDER, RIGHTS.PAY_IS_CONTROLLER],
);
